/*
 * Hero video behaviour for the slideshow section.
 *
 * Rotation is driven here rather than by Dawn's built-in auto-rotate setting.
 * Dawn only renders its pause/play control when auto-rotate is enabled, and we
 * want the banner clean - so auto-rotate stays off and the timing lives here.
 *
 * Behaviour:
 *   - each slide holds for SLIDE_SECONDS, then advances to the next
 *   - going forward, slides glide across over SCROLL_MS with an ease-in-out
 *     curve; wrapping from the last slide back to the first crossfades instead,
 *     so it does not sweep back past every slide
 *   - the visible video restarts from its opening frame each time its slide
 *     comes round, and loops if the slide outlasts the clip
 *   - moving the carousel by hand resets the timer, so a slide the shopper
 *     just chose is not yanked away early
 *
 * Timing is tunable via the three constants below.
 *
 * Performance:
 *   - nothing downloads until after window load, so first paint is unblocked
 *   - only the visible slide and its immediate neighbours are fetched, rather
 *     than every video at once
 *   - off-screen videos are paused so they do not burn CPU or battery
 *
 * Kept in an external file on purpose: Dawn styles `.media > *` with
 * `display: block`, which overrides the browser default of `display: none`
 * on <script> elements and renders inline script source as visible text.
 */
(function () {
  var SLIDE_SECONDS = 8; // how long each slide is held
  var SCROLL_MS = 1100; // how long a forward slide takes
  var WRAP_FADE_MS = 500; // each half of the last-to-first crossfade

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function loadVideo(v) {
    if (!v || v.dataset.heroVideoLoaded === '1' || !v.dataset.heroVideoSrc) return;
    v.dataset.heroVideoLoaded = '1';
    var s = document.createElement('source');
    s.type = 'video/mp4';
    s.src = v.dataset.heroVideoSrc;
    v.appendChild(s);
    v.load();
  }

  function playVideo(v) {
    if (!v) return;
    loadVideo(v);
    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {});
    }
  }

  function pauseVideo(v) {
    if (!v || v.paused) return;
    try {
      v.pause();
    } catch (e) {}
  }

  function setupSlider(slider) {
    if (slider.dataset.heroVideoBound === '1') return;
    slider.dataset.heroVideoBound = '1';

    var videos = Array.prototype.slice.call(
      slider.querySelectorAll('video[data-hero-video]')
    );
    if (!videos.length) return;

    var multi = videos.length > 1;
    var currentIndex = -1;
    var targetIndex = 0;
    var timer = null;
    var frame = null;

    // Loop every clip. A slide may well outlast its video, and a frozen last
    // frame sitting there would look broken.
    for (var i = 0; i < videos.length; i++) {
      videos[i].loop = true;
    }

    function offsetFor(index) {
      return index * (slider.clientWidth || 0);
    }

    /*
     * Two Dawn behaviours interfere with animating scrollLeft frame by frame:
     *   - `scroll-behavior: smooth` in CSS would animate each of our steps
     *   - `scroll-snap-type` would yank the position to the nearest slide
     * Both are suspended for the duration of a move and restored afterwards.
     */
    function suspendNativeScroll() {
      return {
        behavior: slider.style.scrollBehavior,
        snap: slider.style.scrollSnapType,
        restore: function () {
          slider.style.scrollBehavior = this.behavior || '';
          slider.style.scrollSnapType = this.snap || '';
        }
      };
    }

    function stopAnimation() {
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
    }

    function animateTo(index, duration, done) {
      stopAnimation();

      var from = slider.scrollLeft;
      var to = offsetFor(index);
      var delta = to - from;
      var saved = suspendNativeScroll();

      slider.style.scrollBehavior = 'auto';
      slider.style.scrollSnapType = 'none';

      if (Math.abs(delta) < 1 || duration <= 0) {
        slider.scrollLeft = to;
        saved.restore();
        if (done) done();
        return;
      }

      var startedAt = null;

      function step(now) {
        if (startedAt === null) startedAt = now;
        var progress = Math.min((now - startedAt) / duration, 1);
        slider.scrollLeft = from + delta * easeInOutCubic(progress);

        if (progress < 1) {
          frame = window.requestAnimationFrame(step);
        } else {
          frame = null;
          saved.restore();
          if (done) done();
        }
      }

      frame = window.requestAnimationFrame(step);
    }

    /*
     * Wrapping from the last slide to the first: fade out, move while nothing
     * is visible, fade back in. Only the slides fade - Dawn renders the arrows
     * and counter as siblings of this element, so they stay put.
     */
    function crossfadeTo(index) {
      slider.style.transition = 'opacity ' + WRAP_FADE_MS + 'ms ease-in-out';
      slider.style.opacity = '0';

      setTimeout(function () {
        animateTo(index, 0);
        window.requestAnimationFrame(function () {
          slider.style.opacity = '1';
          setTimeout(function () {
            slider.style.transition = '';
            slider.style.opacity = '';
          }, WRAP_FADE_MS + 50);
        });
      }, WRAP_FADE_MS);
    }

    function clearTimer() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    /*
     * targetIndex is tracked separately from the scroll position on purpose.
     * If a move is in flight, or a scroll event is missed, deriving the next
     * slide from scrollLeft alone could stall the rotation.
     */
    function armTimer() {
      clearTimer();
      if (!multi) return;
      timer = setTimeout(function () {
        if (document.hidden) {
          armTimer();
          return;
        }
        var next = (targetIndex + 1) % videos.length;
        if (next < targetIndex) {
          crossfadeTo(next);
        } else {
          animateTo(next, SCROLL_MS);
        }
        targetIndex = next;
        armTimer();
      }, SLIDE_SECONDS * 1000);
    }

    function sync() {
      var width = slider.clientWidth || 1;
      var index = Math.round(slider.scrollLeft / width);
      if (index < 0) index = 0;
      if (index > videos.length - 1) index = videos.length - 1;
      if (index === currentIndex) return;

      currentIndex = index;

      // Keep the rotation in step if the shopper moved the carousel by hand,
      // and give the slide they landed on a full turn before moving on.
      if (index !== targetIndex) {
        targetIndex = index;
        armTimer();
      }

      var active = videos[index];

      loadVideo(active);
      try {
        active.currentTime = 0;
      } catch (e) {}
      playVideo(active);

      // Warm the neighbours so they are ready when they come round.
      loadVideo(videos[index + 1]);
      loadVideo(videos[index - 1]);

      for (var j = 0; j < videos.length; j++) {
        if (j !== index) pauseVideo(videos[j]);
      }
    }

    var ticking = false;
    slider.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      function () {
        currentIndex = -1;
        sync();
      },
      { passive: true }
    );

    sync();
    armTimer();
  }

  function start() {
    var sliders = document.querySelectorAll('.slideshow');
    for (var i = 0; i < sliders.length; i++) {
      setupSlider(sliders[i]);
    }

    var loose = document.querySelectorAll('video[data-hero-video]');
    for (var j = 0; j < loose.length; j++) {
      if (!loose[j].closest('.slideshow')) playVideo(loose[j]);
    }
  }

  function schedule() {
    // In the theme editor, start straight away so the preview looks correct.
    if (window.Shopify && window.Shopify.designMode) {
      start();
      return;
    }
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 2500 });
    } else {
      setTimeout(start, 300);
    }
  }

  if (!window.__heroVideoInit) {
    window.__heroVideoInit = true;
    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  document.addEventListener('shopify:section:load', start);
})();
